import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["rows", "template", "requireOrder", "signOrderOption", "signersSection", "requireSigners", "addSignerButton", "eligibleCountBanner", "filterrificSyncContainer"];
  static values = {
    defaultLabel: String,
  };

  connect() {
    this.draggingRow = null;
    this._prevSignersSectionEnabled = undefined;
    this._signerSwitchSeq = 0;
    this.boundModalShown = () => this.refreshSignerSwitchLabels();
    this.boundModalShow = (e) => this.onModalShow(e);
    this.element.addEventListener("show.bs.modal", this.boundModalShow);
    this.element.addEventListener("shown.bs.modal", this.boundModalShown);

    this.toggleSignersSection();
    this.refreshSignerSwitchLabels();
  }

  disconnect() {
    if (this.boundModalShown) {
      this.element.removeEventListener("shown.bs.modal", this.boundModalShown);
    }
    if (this.boundModalShow) {
      this.element.removeEventListener("show.bs.modal", this.boundModalShow);
    }
  }

  onModalShow(event) {
    this.syncEligibleCountBanner(event);
    this.syncFilterrificHiddenFields();
    this.setDefaultSignersOnOpen();
  }

  setDefaultSignersOnOpen() {
    if (!this.hasRowsTarget || !this.hasTemplateTarget) return;

    this.setDefaultDocumentLabel();

    if (this.hasRequireSignersTarget) {
      this.requireSignersTarget.checked = true;
    }
    if (this.hasRequireOrderTarget) {
      this.requireOrderTarget.checked = false;
    }

    // Primero sincroniza el estado visual/habilitación de la sección.
    // Si venimos de require_signers = false, esta llamada puede recrear 1 fila default.
    this.toggleSignersSection();

    this.rowsTarget.querySelectorAll("[data-signer-row]").forEach((row) => row.remove());

    const first = this.templateTarget.content.cloneNode(true);
    const second = this.templateTarget.content.cloneNode(true);
    this.rowsTarget.appendChild(first);
    this.rowsTarget.appendChild(second);

    const rows = this.rowsTarget.querySelectorAll("[data-signer-row]");
    const workerRow = rows[0];
    const memberRow = rows[1];

    if (workerRow) {
      const employeeCheckbox = workerRow.querySelector("[data-employee-sign]");
      const memberCheckbox = workerRow.querySelector("[data-member-sign]");
      if (employeeCheckbox) employeeCheckbox.checked = true;
      if (memberCheckbox) memberCheckbox.checked = false;
    }

    if (memberRow) {
      const employeeCheckbox = memberRow.querySelector("[data-employee-sign]");
      const memberCheckbox = memberRow.querySelector("[data-member-sign]");
      if (employeeCheckbox) employeeCheckbox.checked = false;
      if (memberCheckbox) memberCheckbox.checked = true;
    }

    this.rowsTarget.querySelectorAll("[data-signer-row]").forEach((row) => {
      this.applySignerMode(row);
      this.assignSignerSwitchIdsForRow(row);
    });
    this.reindexRows();
  }

  setDefaultDocumentLabel() {
    const labelInput = this.element.querySelector('input[name="sign_request[label]"]');
    if (!labelInput) return;

    labelInput.value = this.defaultLabelValue || "";
  }

  syncEligibleCountBanner(event) {
    if (!this.hasEligibleCountBannerTarget) return;
    const trigger = event.relatedTarget;
    if (!trigger?.dataset?.eligibleCount) return;
    const n = trigger.dataset.eligibleCount;
    this.eligibleCountBannerTarget.textContent = `Se enviarán ${n} documentos completados con archivo adjunto y sin envío previo.`;
  }

  syncFilterrificHiddenFields() {
    if (!this.hasFilterrificSyncContainerTarget) return;
    const filterForm = document.getElementById("docx-merge-field-items-filters-form");
    if (!filterForm) return;
    this.filterrificSyncContainerTarget.replaceChildren();
    const stateEl = filterForm.querySelector('input[name="state"]');
    if (stateEl) {
      const stateInput = document.createElement("input");
      stateInput.type = "hidden";
      stateInput.name = "sign_request[view_state]";
      stateInput.value = stateEl.value;
      this.filterrificSyncContainerTarget.appendChild(stateInput);
    }
    const formData = new FormData(filterForm);
    for (const [key, value] of formData.entries()) {
      const m = key.match(/^filterrific\[(.+)\]$/);
      if (!m) continue;
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = `sign_request[filterrific][${m[1]}]`;
      input.value = value;
      this.filterrificSyncContainerTarget.appendChild(input);
    }
  }

  refreshSignerSwitchLabels() {
    if (!this.hasRowsTarget) return;
    this.rowsTarget.querySelectorAll("[data-signer-row]").forEach((row) => this.assignSignerSwitchIdsForRow(row));
  }

  toggleSignersSection() {
    const enabled = this.signersSectionEnabled();
    const reEnableAfterHide = enabled === true && this._prevSignersSectionEnabled === false;

    if (this.hasSignersSectionTarget) {
      this.signersSectionTarget.classList.toggle("d-none", !enabled);
    }

    if (this.hasAddSignerButtonTarget) {
      this.addSignerButtonTarget.disabled = !enabled;
    }

    if (!this.hasRowsTarget) {
      this.syncRequireSignersOrderAvailability();
      this._prevSignersSectionEnabled = enabled;
      return;
    }

    if (!enabled) {
      this.syncRequireSignersOrderAvailability();
      this.rowsTarget.querySelectorAll("input, select, button").forEach((el) => {
        if (el.dataset.docxSignersSavedDisabled === undefined) {
          el.dataset.docxSignersSavedDisabled = String(el.disabled);
        }
        el.disabled = true;
      });
      this.rowsTarget.querySelectorAll("[data-signer-row]").forEach((row) => {
        row.draggable = false;
      });
      this._prevSignersSectionEnabled = false;
      return;
    }

    if (reEnableAfterHide) {
      this.resetSignersRowsToDefault();
    } else {
      this.rowsTarget.querySelectorAll("input, select, button").forEach((el) => {
        if (el.dataset.docxSignersSavedDisabled !== undefined) {
          el.disabled = el.dataset.docxSignersSavedDisabled === "true";
          delete el.dataset.docxSignersSavedDisabled;
        }
      });
    }

    this.rowsTarget.querySelectorAll("[data-signer-row]").forEach((row) => {
      row.draggable = true;
      this.applySignerMode(row);
      this.assignSignerSwitchIdsForRow(row);
    });

    this.reindexRows();
    this.syncRequireSignersOrderAvailability();
    this._prevSignersSectionEnabled = true;
  }

  syncRequireSignersOrderAvailability() {
    const needSigners = this.signersSectionEnabled();

    if (this.hasSignOrderOptionTarget) {
      this.signOrderOptionTarget.classList.toggle("d-none", !needSigners);
    }

    if (!this.hasRequireOrderTarget) {
      if (this.hasRowsTarget) {
        this.toggleOrder();
      }
      return;
    }

    if (!needSigners) {
      this.requireOrderTarget.disabled = true;
      this.requireOrderTarget.checked = false;
    } else {
      this.requireOrderTarget.disabled = false;
    }

    if (this.hasRowsTarget) {
      this.toggleOrder();
    }
  }

  resetSignersRowsToDefault() {
    this.rowsTarget.querySelectorAll("[data-signer-row]").forEach((row) => row.remove());
    const fragment = this.templateTarget.content.cloneNode(true);
    this.rowsTarget.appendChild(fragment);
    this.rowsTarget.querySelectorAll("[data-signer-row]").forEach((row) => this.assignSignerSwitchIdsForRow(row));
  }

  assignSignerSwitchIdsForRow(row) {
    row.querySelectorAll("[data-docx-signer-checkbox]").forEach((wrapper) => {
      const input =
        wrapper.querySelector("input.form-check-input[type='checkbox']") ||
        wrapper.querySelector("input[type='checkbox']");
      const label = wrapper.querySelector("label.form-check-label");
      if (!input || !label) return;

      if (!input.id) {
        this._signerSwitchSeq += 1;
        input.id = `docx_signer_sw_${this._signerSwitchSeq}`;
      }

      label.htmlFor = input.id;
      label.setAttribute("for", input.id);
    });
  }

  signersSectionEnabled() {
    return this.hasRequireSignersTarget ? this.requireSignersTarget.checked : true;
  }

  addSigner() {
    if (!this.signersSectionEnabled()) return;

    const template = this.templateTarget.content.cloneNode(true);
    this.rowsTarget.appendChild(template);
    const newRow = this.rowsTarget.querySelector("[data-signer-row]:last-child");
    if (newRow) this.assignSignerSwitchIdsForRow(newRow);
    this.reindexRows();
    this.toggleOrder();
    if (newRow) this.applySignerMode(newRow);
  }

  removeSigner(event) {
    if (!this.signersSectionEnabled()) return;

    const row = event.currentTarget.closest("[data-signer-row]");
    if (!row) return;

    const totalRows = this.rowsTarget.querySelectorAll("[data-signer-row]").length;
    if (totalRows <= 1) return;

    swalWithBootstrap.fire({
      title: "Eliminar firmante",
      html: "¿Estás seguro de eliminar este firmante?",
    }).then((result) => {
      if (result.isConfirmed) {
        row.remove();
        this.reindexRows();
      }
    });
  }

  toggleOrder() {
    // El orden de configuración debe persistir siempre, independiente de require_signers_order.
    // require_signers_order solo afecta el flujo de firma, no el orden almacenado.
    this.reindexRows();
  }

  syncOrderBadgeVisibility() {
    if (!this.hasRowsTarget) return;
    const show = this.orderEnabled();
    this.rowsTarget.querySelectorAll("[data-order-label]").forEach((el) => {
      el.classList.toggle("d-none", !show);
    });
  }

  toggleEmployeeSign(event) {
    const row = event.currentTarget.closest("[data-signer-row]");
    if (!row) return;

    const employeeCheckbox = row.querySelector("[data-employee-sign]");
    const memberCheckbox = row.querySelector("[data-member-sign]");
    if (employeeCheckbox?.checked && memberCheckbox) {
      memberCheckbox.checked = false;
    }

    this.applySignerMode(row);
    this.assignSignerSwitchIdsForRow(row);
  }

  toggleMemberSign(event) {
    const row = event.currentTarget.closest("[data-signer-row]");
    if (!row) return;

    const employeeCheckbox = row.querySelector("[data-employee-sign]");
    const memberCheckbox = row.querySelector("[data-member-sign]");
    if (memberCheckbox?.checked && employeeCheckbox) {
      employeeCheckbox.checked = false;
    }

    this.applySignerMode(row);
    this.assignSignerSwitchIdsForRow(row);
  }

  onMemberEmailChange(event) {
    const row = event.currentTarget.closest("[data-signer-row]");
    if (!row) return;

    const selectedOption = event.currentTarget.options[event.currentTarget.selectedIndex];
    const nameInput = row.querySelector("[data-name-input]");
    const roleInput = row.querySelector("[data-role-input]");

    if (nameInput && selectedOption?.dataset?.name) {
      nameInput.value = selectedOption.dataset.name;
    }

    if (roleInput && selectedOption?.dataset?.role) {
      roleInput.value = selectedOption.dataset.role;
    }

  }

  reindexRows() {
    this.rowsTarget.querySelectorAll("[data-signer-row]").forEach((row, index) => {
      const orderInput = row.querySelector("[data-order-input]");
      if (orderInput) orderInput.value = index;

      const orderLabel = row.querySelector("[data-order-label]");
      if (orderLabel) orderLabel.textContent = `Orden #${index + 1}`;
    });
    this.syncOrderBadgeVisibility();
  }

  onDragStart(event) {
    if (!this.signersSectionEnabled()) return;

    const row = event.currentTarget.closest("[data-signer-row]");
    if (!row) return;

    this.draggingRow = row;
    row.classList.add("opacity-50");
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", "signer-row");
  }

  onDragOver(event) {
    event.preventDefault();
    const targetRow = event.currentTarget.closest("[data-signer-row]");
    if (!targetRow || targetRow === this.draggingRow) return;

    event.dataTransfer.dropEffect = "move";
    const rect = targetRow.getBoundingClientRect();
    const shouldInsertAfter = event.clientY > rect.top + rect.height / 2;

    if (shouldInsertAfter) {
      this.rowsTarget.insertBefore(this.draggingRow, targetRow.nextElementSibling);
    } else {
      this.rowsTarget.insertBefore(this.draggingRow, targetRow);
    }
  }

  onDrop(event) {
    event.preventDefault();
    this.reindexRows();
    this.toggleOrder();
  }

  onDragEnd() {
    if (this.draggingRow) {
      this.draggingRow.classList.remove("opacity-50");
      this.draggingRow = null;
    }
    this.reindexRows();
    this.toggleOrder();
  }

  applySignerMode(row) {
    const employeeCheckbox = row.querySelector("[data-employee-sign]");
    const memberCheckbox = row.querySelector("[data-member-sign]");
    const nameInput = row.querySelector("[data-name-input]");
    const roleInput = row.querySelector("[data-role-input]");
    const emailInput = row.querySelector("[data-email-input]");
    const memberSelect = row.querySelector("[data-member-email-select]");
    const emailInputWrapper = row.querySelector("[data-email-input-wrapper]");
    const memberSelectWrapper = row.querySelector("[data-member-email-wrapper]");

    if (!employeeCheckbox || !memberCheckbox || !emailInput || !memberSelect) return;

    const isEmployee = employeeCheckbox.checked;
    const isMember = memberCheckbox.checked;
    const emailFieldName = emailInput.dataset.fieldName;
    const roleFieldName = roleInput.dataset.fieldName;

    if (isMember) {
      emailInputWrapper?.classList.add("d-none");
      memberSelectWrapper?.classList.remove("d-none");
      memberSelect.setAttribute("name", emailFieldName);
      emailInput.removeAttribute("name");
      emailInput.value = "";
    } else {
      emailInputWrapper?.classList.remove("d-none");
      memberSelectWrapper?.classList.add("d-none");
      emailInput.setAttribute("name", emailFieldName);
      memberSelect.removeAttribute("name");
      memberSelect.value = "";
    }

    nameInput.disabled = isEmployee;
    roleInput.disabled = isEmployee;
    emailInput.disabled = isEmployee || isMember;
    memberSelect.disabled = !isMember || isEmployee;
    memberCheckbox.disabled = isEmployee;

    if (isEmployee) {
      roleInput.value = "Trabajador";
      nameInput.value = "";
      emailInput.value = "";
      memberSelect.value = "";
    }
  }

  orderEnabled() {
    return this.hasRequireOrderTarget ? this.requireOrderTarget.checked : true;
  }
}
